import React from 'react';
import i18n from '../../../../i18n';
import { Icon } from './Icon';

/** Split "18 May – 14 Jun 2026" into stacked start/end lines. */
export function splitPeriodLabel(label: string): { start: string; end?: string } {
  const sep = ' – ';
  const i = label.indexOf(sep);
  if (i === -1) return { start: label };
  const start = label.slice(0, i).trim();
  const end = label.slice(i + sep.length).trim();
  return { start: withInferredStartYear(start, end), end };
}

/** When the range label omits the start year (same calendar year), show it in the toolbar. */
function withInferredStartYear(start: string, end?: string): string {
  if (!end || /\d{4}/.test(start)) return start;
  const year = end.match(/\d{4}/)?.[0];
  return year ? `${start} ${year}` : start;
}

/** Single-row drill header: close, period, tabs, and headline metrics. */
export function DrillToolbar({
  onClose,
  periodLabel,
  hero,
  children,
}: {
  /** Without it there is no close button (a drawer around the drill has its own). */
  onClose?: () => void;
  periodLabel?: string;
  hero?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const range = periodLabel ? splitPeriodLabel(periodLabel) : null;

  return (
    <div className="drill-toolbar">
      {onClose ? (
        <button
          type="button"
          className="drill-close"
          onClick={onClose}
          aria-label={i18n.t('performance:drillUi.close')}
        >
          <Icon name="close" size={18} />
        </button>
      ) : null}
      {range && (
        <span className="drill-toolbar-period" title={periodLabel}>
          <span className="drill-toolbar-period-line">{range.start}</span>
          {range.end ? (
            <span className="drill-toolbar-period-line drill-toolbar-period-end">
              {range.end}
            </span>
          ) : null}
        </span>
      )}
      {children}
      {hero && <div className="drill-toolbar-hero">{hero}</div>}
    </div>
  );
}

export function DrillToolbarStat({
  label,
  value,
  secondary,
  trend,
}: {
  label: string;
  value: React.ReactNode;
  secondary?: boolean;
  trend?: React.ReactNode;
}) {
  return (
    <div
      className={`drill-toolbar-stat${secondary ? ' drill-toolbar-stat-2' : ''}`}
    >
      <span className="drill-toolbar-stat-lab">{label}</span>
      <span className="drill-toolbar-stat-val">{value}</span>
      {trend}
    </div>
  );
}
